import { isValidTime } from "../dates";
import { isTimePeriod } from "./timePeriods";
import type { Answers, AnswerValue, EntryStatus, MedicationIntake, Question, Scenario, ShowIf } from "../entities";

export type ValidationCode = "required" | "type" | "range" | "option" | "time";

export interface ValidationError {
  code: ValidationCode;
}

export function sameValue(a: AnswerValue, b: AnswerValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
}

/** The value stored when an optional question is skipped. */
export function emptyAnswer(question: Question): AnswerValue {
  switch (question.type) {
    case "multi":
    case "medication":
      return [];
    case "yesno":
      return false;
    case "scale":
    case "number":
      return Number.NaN;
    default:
      return "";
  }
}

const isInteger = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v);

export class ScenarioRunner {
  constructor(private readonly scenario: Scenario) {}

  isRequired(question: Question): boolean {
    return question.required !== false;
  }

  visibleQuestions(answers: Answers): Question[] {
    const visible: Question[] = [];
    for (const question of this.scenario.questions) {
      if (!question.showIf || this.holds(question.showIf, answers, visible)) visible.push(question);
    }
    return visible;
  }

  private holds(condition: ShowIf, answers: Answers, visible: Question[]): boolean {
    if (!visible.some((q) => q.id === condition.questionId)) return false;
    const value = answers[condition.questionId];
    if (value === undefined) return false;
    if (condition.equals !== undefined) return sameValue(value, condition.equals);
    if (condition.in) return condition.in.some((candidate) => sameValue(value, candidate));
    return true;
  }

  next(answers: Answers): Question | null {
    return this.visibleQuestions(answers).find((q) => !(q.id in answers)) ?? null;
  }

  status(answers: Answers): EntryStatus {
    return this.next(answers) === null ? "complete" : "partial";
  }

  progress(answers: Answers): { answered: number; total: number } {
    const visible = this.visibleQuestions(answers);
    return { answered: visible.filter((q) => q.id in answers).length, total: visible.length };
  }

  validate(question: Question, value: AnswerValue | undefined): ValidationError | null {
    const required = this.isRequired(question);
    if (value === undefined) return required ? { code: "required" } : null;
    switch (question.type) {
      case "yesno":
        return typeof value === "boolean" ? null : { code: "type" };
      case "scale":
        if (!isInteger(value)) return { code: "type" };
        return value >= question.min && value <= question.max ? null : { code: "range" };
      case "number": {
        if (typeof value !== "number" || Number.isNaN(value)) return required ? { code: "type" } : null;
        if (question.min !== undefined && value < question.min) return { code: "range" };
        if (question.max !== undefined && value > question.max) return { code: "range" };
        return null;
      }
      case "single":
        if (typeof value !== "string") return { code: "type" };
        if (value === "") return required ? { code: "required" } : null;
        return question.options.some((o) => o.id === value) ? null : { code: "option" };
      case "multi": {
        if (!Array.isArray(value) || !value.every((v) => typeof v === "string")) return { code: "type" };
        if (value.length === 0) return required ? { code: "required" } : null;
        const ids = new Set(question.options.map((o) => o.id));
        const valid = (v: string) =>
          ids.has(v) || (question.allowOther === true && v.startsWith("other:") && v.length > "other:".length);
        return value.every(valid) ? null : { code: "option" };
      }
      case "time":
        if (typeof value !== "string") return { code: "type" };
        if (value === "") return required ? { code: "required" } : null;
        if (question.periods && isTimePeriod(value)) return null;
        return isValidTime(value) ? null : { code: "time" };
      case "text":
        if (typeof value !== "string") return { code: "type" };
        return value.trim() === "" && required ? { code: "required" } : null;
      case "medication": {
        if (!Array.isArray(value)) return { code: "type" };
        const intakes = value as MedicationIntake[];
        if (intakes.length === 0) return required ? { code: "required" } : null;
        for (const intake of intakes) {
          if (typeof intake !== "object" || intake === null || typeof intake.name !== "string") return { code: "type" };
          if (intake.name.trim() === "") return { code: "required" };
          if (!isInteger(intake.count) || intake.count < 1) return { code: "range" };
        }
        return null;
      }
    }
  }
}
